/**
 * Home - Bento Grid Dashboard
 *
 * The main home page component featuring a modern bento grid layout
 * with varied card sizes for visual rhythm and information hierarchy.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import TodaysPromptCard from './TodaysPromptCard';
import StatsCard from './StatsCard';
import RecentReflectionsCard from './RecentReflectionsCard';
import OnThisDayCard from './OnThisDayCard';
import CategoryBreakdownCard from './CategoryBreakdownCard';
import SharedWithMeCard from './SharedWithMeCard';
import FriendSuggestionsCard from './FriendSuggestionsCard';
import { LanternLoader } from '../Skeleton';
import { getLocalHour } from '../../utils/dateUtils';
import './Home.css';

function Home({
  user,
  token,
  categories,
  onNavigateToPrompts,
  onNavigateToReflections,
  onRecordReflection,
  onViewReflection,
  onNavigateToUserShared,
  onNavigateToFriendSuggestion
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [homeData, setHomeData] = useState({
    todaysPrompt: null,
    stats: null,
    recentReflections: [],
    onThisDay: [],
    categoryBreakdown: [],
    sharedWithMe: [],
    friendSuggestionsReceived: []
  });

  const fetchHomeData = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/home');

      setHomeData(response.data);
    } catch (err) {
      console.error('Error fetching home data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHomeData();
  }, [fetchHomeData]);

  const getGreeting = () => {
    const hour = getLocalHour(user?.timezone);
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const handleRecordPrompt = (prompt) => {
    if (onRecordReflection) {
      onRecordReflection(prompt);
    }
  };

  const handleViewReflection = (reflection) => {
    if (onViewReflection) {
      onViewReflection(reflection);
    }
  };

  const handleCategoryClick = (categorySlug) => {
    if (onNavigateToPrompts) {
      onNavigateToPrompts(categorySlug);
    }
  };

  const handleNavigateToUser = (username) => {
    if (onNavigateToUserShared) {
      onNavigateToUserShared(username, false);
    }
  };

  const handleNavigateToUserNewOnly = (username) => {
    if (onNavigateToUserShared) {
      onNavigateToUserShared(username, true);
    }
  };

  const handleFriendSuggestionClick = (promptId) => {
    if (onNavigateToFriendSuggestion) onNavigateToFriendSuggestion(promptId);
  };

  if (loading) {
    return (
      <div className="home-container">
        <LanternLoader text="Loading your dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-container">
        <div className="home-greeting">
          <h1>{getGreeting()}, {user?.username || 'there'}!</h1>
          <p style={{ color: 'var(--color-danger)' }}>{error}</p>
          <button
            className="btn btn--primary"
            onClick={fetchHomeData}
            style={{ marginTop: '1rem' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const {
    todaysPrompt,
    stats,
    recentReflections,
    onThisDay,
    categoryBreakdown,
    sharedWithMe
  } = homeData;
  // friendSuggestionsReceived accessed directly as homeData.friendSuggestionsReceived

  return (
    <div className="home-container">
      <div className="home-greeting">
        <h1>{getGreeting()}, {user?.username || 'there'}!</h1>
        <p>What would you like to reflect on today?</p>
      </div>

      <div className="bento-grid">
        {/* Row 1: Today's Prompt (large) + Stats (medium) */}
        <TodaysPromptCard
          prompt={todaysPrompt}
          categories={categories}
          onRecord={handleRecordPrompt}
          onViewPrompts={onNavigateToPrompts}
        />

        <StatsCard stats={stats} />

        {/* Row 2: Recent Reflections + On This Day + Categories */}
        <RecentReflectionsCard
          reflections={recentReflections}
          onViewReflection={handleViewReflection}
          onViewAll={onNavigateToReflections}
          timezone={user?.timezone}
        />

        <OnThisDayCard
          memories={onThisDay}
          onViewMemory={handleViewReflection}
          timezone={user?.timezone}
        />

        {/* Row 3: Category Breakdown + Shared With Me */}
        <CategoryBreakdownCard
          breakdown={categoryBreakdown}
          categories={categories}
          onCategoryClick={handleCategoryClick}
        />

        <SharedWithMeCard
          sharedWithMe={sharedWithMe}
          onNavigateToUser={handleNavigateToUser}
          onNavigateToUserNewOnly={handleNavigateToUserNewOnly}
        />

        <FriendSuggestionsCard
          friendSuggestions={homeData.friendSuggestionsReceived}
          onNavigate={handleFriendSuggestionClick}
        />
      </div>
    </div>
  );
}

export default Home;
